<script lang="ts" setup>
import { ref } from 'vue';
import { trackingOnboardingEvent } from '@/api/tracking';
import { PRODUCT_LOGO_ASSET, PRODUCT_LOGO_STYLE } from '@/constants';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import HelpDropdown from '@/components/menu/help-dropdown.vue';
import NavigationDropdown from '@/components/menu/navigation-dropdown.vue';
import ArticleShare from '@/components/modals/article-share.vue';
import NotificationButton from '@/components/notifications/header/notification-button.vue';
import KeywordSearchBar from '@/components/search/keyword-search-bar.vue';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import {
  isOptedOutOfNextLayout,
  switchLayout,
} from '@/utils/composables/useLayoutPreference';

interface Props {
  enableKeywordSearchBar?: boolean;
  enableArticleShare?: boolean;
  serviceName: string;
  topRouteName: string;
  supportLink?: string;
}

withDefaults(defineProps<Props>(), {
  enableKeywordSearchBar: true,
  enableArticleShare: true,
});

// v-ifとv-model:openで使っている
// v-ifはunmountしてstateをリセットするために使っている
// MentionAutocompleteTextareaはunmountされないことでリセットするのは難しい
const isArticleShareOpen = ref(false);

const showArticleShareModal = () => {
  isArticleShareOpen.value = true;
};

const canReturnToNextLayout = isOptedOutOfNextLayout();

const { getCurrentPathForTracking } = useCurrentPathForTracking();
const trackingClickRestart = async () => {
  await trackingOnboardingEvent('onboarding_restart_clicked', {
    page: {
      url: getCurrentPathForTracking(),
    },
    feature: 'onboarding_restart',
  });
};
</script>

<template>
  <div class="o-feed-header">
    <div class="brand-section">
      <NavigationDropdown />
      <router-link class="o-anews-logo-link" :to="{ name: topRouteName }"
        ><img
          class="p-anews-logo"
          :src="PRODUCT_LOGO_ASSET"
          :style="PRODUCT_LOGO_STYLE"
        />
        <div class="c-title-bold--s o-service-subtitle">
          {{ serviceName }}
        </div>
      </router-link>
    </div>
    <KeywordSearchBar v-if="enableKeywordSearchBar"></KeywordSearchBar>
    <div class="o-navigation">
      <div class="o-navigation-icon-list">
        <button
          v-if="canReturnToNextLayout"
          class="c-btn c-btn--small c-btn--auto c-btn--AnewsPrimary"
          @click="switchLayout(false)"
          v-tooltip.bottom="{
            content:
              'ホーム画面のデザインをリニューアルしました。新しいデザインをぜひお試しいただき、ご意見をお聞かせください。いつでも旧デザインに戻すことができます。',
            theme: 'small',
          }"
        >
          新デザインを試す
        </button>
        <NotificationButton />
        <button
          v-if="enableArticleShare"
          class="o-article-share-icon"
          v-tooltip.top="'外部リンクを共有'"
          @click="showArticleShareModal"
        >
          <DgrIcon name="clip-plus" class="o-icon" />
        </button>
        <HelpDropdown>
          <template #dropdown-items>
            <hr class="dropdown-divider" />
            <RouterLink
              :to="{ name: 'onboardingWelcome' }"
              @click="trackingClickRestart"
              class="dropdown-item"
            >
              セットアップをやり直す
            </RouterLink>
          </template>
        </HelpDropdown>
      </div>
    </div>
    <ArticleShare
      v-if="isArticleShareOpen"
      v-model:open="isArticleShareOpen"
    ></ArticleShare>
  </div>
</template>
<style lang="scss" scoped>
.o-feed-header {
  position: fixed;
  top: 0;
  left: 0;
  z-index: var(--z-header);
  display: flex;
  flex-direction: row;
  align-items: center;
  height: $header-height;
  width: 100%;
  background-color: white;
  box-shadow: 0px 1px 5px rgba($color-gray1000, 0.25);
  box-sizing: border-box;
  padding: 0 12px;

  .brand-section {
    display: flex;
    align-items: center;
  }

  .o-anews-logo-link {
    display: flex;
    padding-bottom: 7px;
    align-items: end;
    height: 32px;
    box-sizing: border-box;
  }

  .o-service-subtitle {
    display: flex;
    align-items: end;
    height: 32px;
    line-height: 1;
    box-sizing: border-box;
    margin-left: 8px;
    flex-shrink: 0;
  }

  .o-navigation {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    flex-grow: 1;

    .o-navigation-icon-list {
      display: flex;
      flex-direction: row;
      align-items: center;
      width: auto;
      height: 64px;
      gap: 8px;
      .o-bell-icon,
      .o-article-share-icon,
      .o-help,
      .o-user-icon {
        position: relative;
        width: 32px;
        height: 32px;
        display: flex;
        flex-direction: row;
        align-items: center;
        padding: 0;
        border-radius: 4px;
        border: none;
        background-color: white;

        &:hover {
          background-color: $color-gray200;
        }

        .o-icon {
          width: 21px;
          height: 21px;
          margin: 5.5px;
        }
      }

      // 旧デザインの見た目は変えないため、新しいヘッダーに合わせたヘルプのアイコンの大きさをここだけ戻す
      :deep(.help-menu) {
        padding: 5.5px;
        .icon {
          width: 28px;
          height: 28px;
        }
      }
    }
  }
}

.o-icon {
  width: 28px;
  height: 28px;
}

.dropdown-item {
  padding: 4px 12px;

  &:hover {
    background: $color-gray200;
  }
}

.dropdown-divider {
  border-top: 1px solid $color-gray400;
  border-bottom: 0;
}
</style>
